import { TimeMilliseconds } from '@freelancer/datastore/core';
import { TaskApi } from 'api-typings/tasklist/tasklist';

// This file contains all the types for the notifications and the project feed.
// The corresponding backend types for websocket messages can be found in
// `@freelancer/datastore/core/websocket/server-message-event-model.ts`.

export interface NotificationContestData {
  readonly isTest: boolean;
  readonly jobString: string;
  readonly linkUrl: string;
  readonly time: number;
  readonly title: string;
  readonly userId: number;
  readonly username: string;
}

export interface NotificationProjectData {
  readonly appendedDescr: string;
  readonly imgUrl: string;
  readonly isTest: boolean;
  readonly jobs: ReadonlyArray<number>;
  readonly jobString: string;
  readonly linkUrl: string;
  readonly text: string;
  readonly title: string;
  readonly userId: number;
  readonly username: string;
}

export interface NotificationArticleComment {
  readonly type: 'articleCommentReceived';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly articleAuthor: number; // The ID of author
    readonly articleName: string;
    readonly articleType: string; // Some enum?
    readonly commentBy: number;
    readonly commentId: number;
    readonly comment: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName: string;
    readonly user: string;
  };
}

export interface NotificationAward {
  readonly type: 'award';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl?: string;
    readonly linkUrl?: string;
    readonly title: string;
    readonly username: string;
  };
}

export interface NotificationAwardBadge {
  readonly type: 'awardBadge';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly descr: string;
    readonly imgUrl: undefined;
    readonly linkUrl: string;
    readonly name: string;
  };
}

export interface NotificationAwardCredit {
  readonly type: 'awardCredit';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly amount: number;
    readonly imgUrl: undefined;
    readonly linkUrl: string;
  };
}

export interface NotificationAwardMilestoneReminder {
  readonly type: 'awardMilestoneReminder';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly appendedDescr: string;
    readonly currencyCode: string;
    readonly currencyId: string | number;
    readonly currencySign: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly title: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationAwardReminder {
  readonly type: 'awardReminder';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly name: string;
    readonly username: string;
    readonly linkUrl: string;
    readonly imgUrl: string;
  };
}

export interface NotificationAwardXp {
  readonly type: 'awardXp';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly amount: number;
    readonly descr: string;
    readonly imgUrl: undefined;
    readonly linkUrl: string;
  };
}

export interface NotificationBid {
  readonly type: 'bid';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly amount: string;
    readonly bidAvg?: number;
    readonly bidCount?: number;
    readonly currency: string;
    readonly imgUrl: string;
    readonly linkUrl?: string;
    readonly projName: string;
    readonly publicName?: string;
    readonly title: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationContest {
  readonly type: 'contest';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationContestData & {
    readonly buyer: string;
    readonly buyerUrl?: string;
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangeRate: string;
    readonly imgUrl: string;
    readonly jobs: ReadonlyArray<number>;
    readonly minBudget?: number;
    readonly maxBudget?: number;
  };
}

export interface NotificationContestAwardedToEmployer {
  readonly type: 'contestAwardedToEmployer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestName: string;
    readonly contestSeoUrl?: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entryId: string;
    readonly entryNumber: string;
    readonly entryCount: string;
    readonly freelancerCount: string;
    readonly imgUrl: string;
    readonly isBought: boolean;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly thumb: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationContestAwardedToFreelancer {
  readonly type: 'contestAwardedToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestName: string;
    readonly contestSeoUrl: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly entryId: string | number;
    readonly entryNumber: string | number;
    readonly entryCount: string | number;
    readonly freelancerCount: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly prize: string | number;
    readonly publicName?: string;
    readonly userId: string | number;
    readonly username: string;
  };
}

export interface NotificationContestEntryBoughtToEmployer {
  readonly type: 'contestEntryBoughtToEmployer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestName: string;
    readonly entryId: string | number;
    readonly entryNumber: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly username: string;
  };
}

export interface NotificationContestEntryBoughtToFreelancer {
  readonly type: 'contestEntryBoughtToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestName: string;
    readonly entryId: string | number;
    readonly entryNumber: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly publicName?: string;
    readonly username: string;
  };
}

export interface NotificationContestEntry {
  readonly type: 'contestEntry';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string;
    readonly secondUserPublicName?: string;
    readonly userId: number;
  };
}

export interface NotificationContestEntryRatedToFreelancer {
  readonly type: 'contestEntryRatedToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly action: string;
    readonly contestId: number;
    readonly contestName: string;
    readonly employerId: number;
    readonly entriesUrl: string;
    readonly entryId: number;
    readonly entryNumber: number;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: number;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface NotificationContestEntryRejectedToFreelancer {
  readonly type: 'contestEntryRejectedToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly action: string;
    readonly contestId: number;
    readonly contestName: string;
    readonly employerId: number;
    readonly entriesUrl: string;
    readonly entryId: number;
    readonly entryNumber: number;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: number;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface NotificationContestEntryReconsideredToFreelancer {
  readonly type: 'contestEntryReconsideredToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly action: string;
    readonly contestId: number;
    readonly contestName: string;
    readonly employerId: number;
    readonly entriesUrl: string;
    readonly entryId: number;
    readonly entryNumber: number;
    readonly entryS3path: string;
    readonly entryThumb: string;
    readonly entryUrl: string;
    readonly freelancerId: number;
    readonly hasOtherEntriesRatedOrRejected: boolean;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly ratedOrRejectedEntriesCount: number;
    readonly rating: string;
  };
}

export interface NotificationContestEntryCommentedToFreelancer {
  readonly type: 'contestEntryCommentedToFreelancer';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestId: number;
    readonly contestName: string;
    readonly entryNumber: number;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface NotificationContestPCBNotification {
  readonly type: 'contestPCBNotification';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestHolder: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly firstUser?: string;
    readonly firstUserPublicName?: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string;
    readonly secondUserPublicName?: string;
    readonly userId: number;
    readonly userCount: number;
  };
}

export interface NotificationContestPCBNotificationFullView {
  readonly type: 'contestPCBNotificationFullView';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestHolder: string | number;
    readonly contestName: string;
    readonly entryNumber?: string | number;
    readonly firstUser: string;
    readonly firstUserPublicName?: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly secondUser?: string;
    readonly secondUserPublicName?: string;
    readonly userId: string | number;
    readonly userCount: number;
  };
}

export interface NotificationCreateMilestone {
  readonly type: 'createMilestone';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly accountId?: number;
    readonly amount: string | number;
    readonly bidId: string | number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly projectId: string | number;
    readonly publicName?: string;
    readonly shortDescr: string;
    readonly title: string;
    readonly transId: string | number;
  };
}

export interface NotificationDenyed {
  readonly type: 'denyed';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl: string;
    readonly jobsURL: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly userId: string | number;
    readonly username: string;
  };
}

export interface NotificationDraftContest {
  readonly type: 'draftContest';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly contestEditUrl?: string;
    readonly contestId: string;
    readonly contestName: string;
    readonly linkUrl: string;
    readonly imgUrl: string;
    readonly isPublished: boolean;
  };
}

export interface NotificationFailingProject {
  readonly type: 'failingProject';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly address?: string;
    readonly currency?: string;
    readonly currencyCode: string;
    readonly featured: boolean;
    readonly freeBidUntil?: number;
    readonly fulltime: boolean;
    readonly maxBudget?: number;
    readonly minBudget?: number;
    readonly nonpublic: boolean;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
  };
}

export interface NotificationGiveGetChildReceivedSignupBonus {
  readonly type: 'giveGetChildReceivedSignupBonus';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface NotificationGiveGetChildSignUp {
  readonly type: 'giveGetChildSignUp';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonus: number;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface NotificationGiveGetChildPartialMilestoneRelease {
  readonly type: 'giveGetChildPartialMilestoneRelease';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly parentId: number;
    readonly childId: number;
    readonly childName: string;
    readonly bonusRequirement: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface NotificationGiveGetParentBonus {
  readonly type: 'giveGetParentBonus';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly parentId: number;
    readonly bonus: number;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export interface NotificationHireMe {
  readonly type: 'hireMe';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly currency?: { readonly code: string; readonly sign: string };
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly period?: number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly username: string;
    readonly title: string;
    readonly sum?: number;
  };
}

export interface NotificationNotifyFollower {
  readonly type: 'notifyfollower';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData;
}

export interface NotificationHighLtvAnnualDiscount {
  readonly type: 'highLtvAnnualDiscount';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl: undefined;
    readonly linkUrl: string;
    readonly userDisplayName: string;
  };
}

export interface NotificationInviteUserBid {
  readonly type: 'inviteUserBid';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly inviteeId: number;
    readonly inviteeName: string;
    readonly maxBudget?: number;
    readonly minBudget?: number;
    readonly publicName?: string;
  };
}

export interface NotificationInviteToContest {
  readonly type: 'inviteToContest';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly buyer: string | number; // The ID
    readonly buyerName: string;
    readonly buyerPublicName?: string;
    readonly buyerUrl: string;
    readonly contestRecommendedSkills: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly description: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly prize: string | number;
    readonly sellerId: string | number;
    readonly sellerName: string;
    readonly sellerPublicName?: string;
    readonly title: string;
    readonly type?: string;
  };
}

export interface NotificationInvoiceRequested {
  readonly type: 'invoiceRequested';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly accountId: number;
    readonly amount: number;
    readonly bidId: number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly imgUrl: string;
    readonly invoiceId: number;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly sellerId: number;
    readonly seoUrl: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationLevelUp {
  readonly type: 'levelUp';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl: undefined;
    readonly level: string;
    readonly linkUrl: string;
    readonly membership: boolean;
    readonly perks: ReadonlyArray<{
      readonly description: string;
      readonly item: boolean;
      readonly magnitude: number;
      readonly name?: 'BidRefreshRate' | 'item';
      readonly symbol: string;
    }>;
    readonly username: string;
  };
}

export interface NotificationLocalJobPosted {
  readonly type: 'localJobPosted';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangeRate: string;
    readonly featured: boolean;
    readonly freeBidUntil?: number;
    readonly fulltime: boolean;
    readonly isLocal: true;
    readonly maxBudget?: number;
    readonly minBudget?: number;
    readonly nonpublic: boolean;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
  };
}

export interface NotificationRecruiterProject {
  readonly type: 'recruiterProject';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly address?: string;
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangeRate: string;
    readonly featured: boolean;
    readonly freeBidUntil?: number;
    readonly fulltime: boolean;
    readonly isLocal: boolean;
    readonly maxBudget?: number;
    readonly minBudget?: number;
    readonly nonpublic: boolean;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
  };
}

export interface NotificationProject {
  readonly type: 'project';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly address?: string;
    readonly currency?: string;
    readonly currencyCode: string;
    readonly exchangeRate: string;
    readonly featured: boolean;
    readonly freeBidUntil?: number;
    readonly fulltime: boolean;
    readonly maxBudget?: number;
    readonly minBudget?: number;
    readonly nonpublic: boolean;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly submitDate: string;
    readonly usdMaxbudget?: number;
    readonly usdMinbudget?: number;
  };
}

export interface NotificationProjectCompleted {
  readonly type: 'completed';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly amount: string | number;
    readonly categoryName: string;
    readonly completeStatus: string; // TODO: T37445 Make this a string enum
    readonly currencyCode: string;
    readonly currencyId: string | number;
    readonly currencySign: string;
    readonly employerId: string | number;
    readonly employerPublicName?: string;
    readonly employerUsername: string;
    readonly expiredTimestamp: string;
    readonly freelancerId: string | number;
    readonly freelancerPublicName?: string;
    readonly freelancerUsername: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly selectionId: string | number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
  };
}

export interface NotificationQuickHireProject {
  readonly type: 'quickHireProject';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationProjectData & {
    readonly currency?: { readonly code: string; readonly sign: string };
    readonly period?: number;
    readonly projIsHourly: boolean;
    readonly publicName?: string;
    readonly sum?: number;
  };
}

export interface NotificationReleaseMilestone {
  readonly type: 'releaseMilestone';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly accountId: string | number;
    readonly amount: string | number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly otherReason: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly submitDate: string;
    readonly title: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationRequestEndProject {
  readonly type: 'requestEndProject';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly accountId: string | number;
    readonly bidId: string | number;
    readonly categoryName: string;
    readonly dayCount: string | number;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly submitDate: string;
    readonly userId: string | number;
    readonly username: string;
  };
}

export interface NotificationRequestMilestone {
  readonly type: 'requestMilestone';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly accountId: number;
    readonly amount: number;
    readonly bidId: number;
    readonly currencyCode: string;
    readonly currencyId: number;
    readonly currencySign: string;
    readonly description: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly requestId: number;
    readonly sellerId: number;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly submitDate: string;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationRequestToRelease {
  readonly type: 'requestToRelease';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly accountId: number;
    readonly amount: number;
    readonly bidId: number;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly descr: string;
    // img: ???;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly name: string;
    readonly publicName?: string;
    readonly seoUrl: string;
    readonly state?: string; // TODO: T37445 Make this a string enum
    readonly tranId: number;
    readonly userId: number;
    readonly username: string;
  };
}

export interface NotificationServiceProjectEndingSeller {
  readonly type: 'serviceProjectEndingSeller';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly bidId: number;
    readonly buyerId: number;
    readonly buyerName: string;
    readonly hoursRemaining: number;
    readonly imgUrl: undefined;
    readonly linkUrl: string;
    readonly orderStartDate: string;
    readonly projectId: number;
    readonly projectName: string;
    readonly projectUrl: string;
    readonly sellerId: number;
    readonly sellerName: string;
    readonly serviceId: number;
    readonly serviceName: string;
  };
}

export interface NotificationServiceProjectPostedSeller {
  readonly type: 'serviceProjectPostedSeller';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly bidId: number;
    readonly buyerId: number;
    readonly buyerImgUrl: string;
    readonly buyerName: string;
    readonly buyerPublicName?: string;
    readonly currencyCode: string;
    readonly currencySign: string;
    readonly imgUrl: string;
    readonly linkUrl: string;
    readonly orderEndDate: string;
    readonly orderStartDate: string;
    readonly projectCost: number;
    // project_duration: ???;
    readonly projectId: number;
    // project_milestones: ???;
    readonly projectName: string;
    readonly projectUrl: string;
    readonly sellerId: number;
    readonly sellerImgUrl: string;
    readonly sellerName: string;
    readonly sellerPublicName: string;
    readonly serviceId: number;
    readonly serviceName: string;
    readonly serviceUrl: string;
  };
}

export interface NotificationServiceData {
  readonly imgUrl: undefined;
  readonly linkUrl: string;
  readonly serviceId: number;
  readonly serviceName: string;
  readonly serviceUrl: string;
  readonly serviceDuration: string;
  readonly serviceCost: number;
  readonly currencySign: string;
  readonly currencyCode: string;
  readonly serviceImg: string; // A URL
}

export interface NotificationServiceApproved {
  readonly type: 'serviceApproved';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationServiceData;
}

export interface NotificationServiceRejected {
  readonly type: 'serviceRejected';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationServiceData;
}

export interface NotificationSignUpFreeTrialUpsell {
  readonly type: 'signUpFreeTrialUpsell';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl: undefined;
    readonly linkUrl: string;
    readonly trialPackageName: string;
  };
}

export interface NotificationUpsellCrowdsourceFinding {
  readonly type: 'upsellCrowdsourceFinding';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: {
    readonly imgUrl: undefined;
    readonly linkUrl: undefined;
    readonly projectName: string;
  };
}

export interface NotificationXpContest {
  readonly type: 'xpContest';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: NotificationContestData & {
    readonly currency: string;
    readonly currencyCode: string;
    readonly imgUrl: string;
    readonly minBudget?: number;
    readonly maxBudget?: number;
  };
}

export interface NotificationTaskCreate {
  readonly type: 'tasklistCreateV1';
  readonly parent_type: 'notifications';
  readonly id: string;
  readonly data: TaskApi & {
    readonly imgUrl: string;
    readonly linkUrl: string;
  };
}

export type NotificationEntry = TimeMilliseconds &
  (
    | NotificationArticleComment
    | NotificationAward & { readonly state?: string } // TODO: T37445 Make this a string enum
    | NotificationAwardBadge
    | NotificationAwardCredit
    | NotificationAwardMilestoneReminder
    | NotificationAwardReminder
    | NotificationAwardXp
    | NotificationBid
    | NotificationContestAwardedToEmployer
    | NotificationContestAwardedToFreelancer
    | NotificationContestEntry
    | NotificationContestEntryBoughtToEmployer
    | NotificationContestEntryBoughtToFreelancer
    | NotificationContestEntryCommentedToFreelancer
    | NotificationContestEntryRatedToFreelancer
    | NotificationContestEntryReconsideredToFreelancer
    | NotificationContestEntryRejectedToFreelancer
    | NotificationContestPCBNotification
    | NotificationContestPCBNotificationFullView
    | NotificationCreateMilestone
    | NotificationDenyed
    | NotificationDraftContest
    | NotificationGiveGetChildPartialMilestoneRelease
    | NotificationGiveGetChildReceivedSignupBonus
    | NotificationGiveGetChildSignUp
    | NotificationGiveGetParentBonus
    | NotificationHighLtvAnnualDiscount
    | NotificationHireMe
    | NotificationInviteToContest
    | NotificationInviteUserBid
    | NotificationInvoiceRequested
    | NotificationLevelUp
    | NotificationNotifyFollower
    | NotificationProjectCompleted
    | NotificationQuickHireProject
    | NotificationReleaseMilestone
    | NotificationRequestEndProject
    | NotificationRequestMilestone
    | NotificationRequestToRelease
    | NotificationServiceApproved
    | NotificationServiceProjectEndingSeller
    | NotificationServiceProjectPostedSeller
    | NotificationServiceRejected
    | NotificationSignUpFreeTrialUpsell
    | NotificationTaskCreate
    | NotificationUpsellCrowdsourceFinding);

/** There are certain newsfeed notifications that we want to ignore,
 * so we map them to `NotificationEntrySkipped`.
 * We have to map them to something otherwise asking for 20 might only return
 * 15 which would break pagination.
 */
export type NotificationEntrySkipped = TimeMilliseconds & {
  readonly type: undefined;
  readonly parent_type: 'notifications';
  readonly id: string;
};

export function isNotificationEntry(
  notification: NotificationEntry | NotificationEntrySkipped,
): notification is NotificationEntry {
  return notification.type !== undefined;
}
